import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  EventAvailable,
  Refresh,
  CalendarMonth,
} from '@mui/icons-material'
import { holidayAPI, Holiday } from '../../services/api'

const HolidayList: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    fetchUpcomingHolidays()
  }, [])

  const fetchUpcomingHolidays = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await holidayAPI.getUpcomingHolidays(30)
      setHolidays(data)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch holidays')
      console.error('Error fetching holidays:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSync = async () => {
    setIsSyncing(true)
    setError(null)
    try {
      const currentYear = new Date().getFullYear()
      await holidayAPI.syncHolidays(currentYear)
      // Refresh the list after sync
      await fetchUpcomingHolidays()
    } catch (err: any) {
      setError(err.message || 'Failed to sync holidays')
      console.error('Error syncing holidays:', err)
    } finally {
      setIsSyncing(false)
    }
  }

  const getHolidayTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'public':
        return '#FFB74D' // Orange
      case 'bank':
        return '#64B5F6' // Blue
      case 'mercantile':
        return '#81C784' // Green
      case 'poya':
        return '#BA68C8' // Purple
      default:
        return '#90A4AE' // Grey
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)
    const diffTime = date.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays < 0) return 'Past'
    return `in ${diffDays} days`
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f7fa', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #FFB74D 0%, #F57C00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(255, 183, 77, 0.3)',
            }}
          >
            <EventAvailable sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: 'text.primary' }}>
              Upcoming Holidays
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Next 30 days
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={isSyncing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleSync}
          disabled={isSyncing}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          {isSyncing ? 'Syncing...' : 'Sync Holidays'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Holiday List */}
      <Card sx={{
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        flex: 1,
        overflow: 'auto',
        background: 'white',
      }}>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : holidays.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CalendarMonth sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Upcoming Holidays
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are no holidays in the next 30 days
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {holidays.map((holiday, index) => (
                <React.Fragment key={holiday.id}>
                  <ListItem
                    sx={{
                      py: { xs: 2, sm: 2.5 },
                      px: { xs: 2, sm: 3 },
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          bgcolor: getHolidayTypeColor(holiday.holiday_type),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 1,
                        }}
                      >
                        <EventAvailable sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                            {holiday.name}
                          </Typography>
                          <Chip
                            label={holiday.holiday_type}
                            size="small"
                            sx={{
                              bgcolor: getHolidayTypeColor(holiday.holiday_type),
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                          {!holiday.is_working_day && (
                            <Chip
                              label="Day Off"
                              size="small"
                              sx={{
                                bgcolor: '#4CAF50',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                            {formatDate(holiday.date)}
                          </Typography>
                          <Chip
                            label={getDaysUntil(holiday.date)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 20,
                              borderColor: 'divider',
                            }}
                          />
                        </Box>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  {index < holidays.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Box sx={{
        mt: { xs: 2, sm: 3 },
        display: 'flex',
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 1.5 },
        justifyContent: 'center',
        p: 2,
        bgcolor: 'white',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      }}>
        <Chip
          size="small"
          label="Public Holiday"
          sx={{
            backgroundColor: getHolidayTypeColor('public'),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            height: { xs: 24, sm: 28 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="small"
          label="Bank Holiday"
          sx={{
            backgroundColor: getHolidayTypeColor('bank'),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            height: { xs: 24, sm: 28 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="small"
          label="Mercantile Holiday"
          sx={{
            backgroundColor: getHolidayTypeColor('mercantile'),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            height: { xs: 24, sm: 28 },
            borderRadius: '12px',
          }}
        />
        <Chip
          size="small"
          label="Poya Day"
          sx={{
            backgroundColor: getHolidayTypeColor('poya'),
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            height: { xs: 24, sm: 28 },
            borderRadius: '12px',
          }}
        />
      </Box>
    </Box>
  )
}

export default HolidayList
